import React, { useState, useEffect } from 'react';
import { X, Mail, Inbox, UserPlus, ArrowRight, RefreshCw, Check, AlertCircle, Trash2, Plus } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { fetchInboundEmails, getGmailStatus, deleteInboundEmail, addManualInboundEmail } from '../lib/emailService';
import { todayKey } from '../lib/dateHelpers';

/**
 * InboundEmailModal
 *
 * Pulls emails from the app's intake inbox (forwarded enquiries).
 * Flow: Forward email to app address → Open modal → Select email → Create enquiry
 *
 * Demo mode: shows simulated inbound emails
 * Live mode: fetches from Gmail via Supabase Edge Function
 */
const InboundEmailModal = ({ isOpen, onClose }) => {
  const { events, venues, addEvent, addSentEmail } = useAppContext();
  const gmailStatus = getGmailStatus();

  // Inbox state
  const [inboxEmails, setInboxEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [processedIds, setProcessedIds] = useState(() => {
    try {
      const stored = localStorage.getItem('errigal_processed_inbound');
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });

  // Event creation form (step 2)
  const [eventForm, setEventForm] = useState({
    eventName: '',
    eventType: 'private_party',
    eventDate: '',
    venueId: '',
    guests: 0,
    notes: '',
    linkToExisting: false,
    existingEventId: '',
  });

  // Manual email form
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualEmailForm, setManualEmailForm] = useState({
    fromName: '',
    fromAddress: '',
    subject: '',
    body: '',
    date: todayKey(),
  });
  const [addingEmail, setAddingEmail] = useState(false);

  const [step, setStep] = useState(1); // 1 = inbox, 2 = create event
  const [submitted, setSubmitted] = useState(false);

  // Persist processed IDs
  useEffect(() => {
    try { localStorage.setItem('errigal_processed_inbound', JSON.stringify(processedIds)); } catch {}
  }, [processedIds]);

  // Fetch inbox on open
  useEffect(() => {
    if (isOpen) {
      handleRefresh();
    }
  }, [isOpen]);

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const emails = await fetchInboundEmails();
      // Filter out already-processed emails
      const unprocessed = emails.filter((em) => !processedIds.includes(em.id));
      setInboxEmails(unprocessed);
    } catch (err) {
      console.error('Failed to fetch inbound emails:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEmail = async (email, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete email from ${email.fromName || email.from}?`)) {
      return;
    }
    try {
      const result = await deleteInboundEmail(email.id);
      if (result.success) {
        setInboxEmails((prev) => prev.filter((em) => em.id !== email.id));
      } else {
        alert(`Failed to delete email: ${result.error}`);
      }
    } catch (err) {
      console.error('Delete error:', err);
      alert('Failed to delete email');
    }
  };

  const handleManualEmailFormChange = (e) => {
    const { name, value } = e.target;
    setManualEmailForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddManualEmail = async () => {
    if (!manualEmailForm.fromAddress || !manualEmailForm.subject) {
      alert('Please fill in From Email and Subject');
      return;
    }

    setAddingEmail(true);
    try {
      // Convert date string to ISO timestamp
      const isoDate = new Date(manualEmailForm.date).toISOString();
      const result = await addManualInboundEmail({
        fromName: manualEmailForm.fromName,
        fromAddress: manualEmailForm.fromAddress,
        subject: manualEmailForm.subject,
        body: manualEmailForm.body,
        date: isoDate,
      });

      if (result.success) {
        setInboxEmails((prev) => [result.email, ...prev]);
        setShowManualForm(false);
        setManualEmailForm({
          fromName: '',
          fromAddress: '',
          subject: '',
          body: '',
          date: todayKey(),
        });
      } else {
        alert(`Failed to add email: ${result.error}`);
      }
    } catch (err) {
      console.error('Add manual email error:', err);
      alert('Failed to add email');
    } finally {
      setAddingEmail(false);
    }
  };

  const handleSelectEmail = (email) => {
    setSelectedEmail(email);
    // Auto-generate event name from subject
    const autoName = email.subject || `Enquiry from ${email.fromName || email.from}`;
    // Try to detect event type from subject/body
    const combined = `${email.subject} ${email.body}`.toLowerCase();
    let autoType = 'private_party';
    if (combined.includes('wedding')) autoType = 'wedding';
    else if (combined.includes('corporate') || combined.includes('conference') || combined.includes('meeting') || combined.includes('team')) autoType = 'corporate';

    // Try to extract guest count from body
    const guestMatch = (email.body || '').match(/(\d+)\s*(?:guests|people|pax|attendees)/i);
    const autoGuests = guestMatch ? parseInt(guestMatch[1]) : 0;

    // Try to extract phone from body
    const phoneMatch = (email.body || '').match(/(?:0\d{1,2}[\s-]?\d{3}[\s-]?\d{4}|\+353[\s-]?\d{2,3}[\s-]?\d{3}[\s-]?\d{3,4})/);

    setEventForm({
      eventName: autoName,
      eventType: autoType,
      eventDate: '',
      venueId: '',
      guests: autoGuests,
      notes: phoneMatch ? `Phone: ${phoneMatch[0]}` : '',
      linkToExisting: false,
      existingEventId: '',
    });
    setStep(2);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEventForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = () => {
    if (!selectedEmail) return;

    let eventId;
    let eventName;

    if (eventForm.linkToExisting && eventForm.existingEventId) {
      eventId = eventForm.existingEventId;
      const existing = events.find((e) => e.id === eventId);
      eventName = existing?.name || 'Unknown Event';
    } else {
      if (!eventForm.eventName) return;
      const newEvent = addEvent({
        name: eventForm.eventName,
        client: selectedEmail.fromName || selectedEmail.from.split('@')[0],
        type: eventForm.eventType,
        date: eventForm.eventDate || '',
        venue_id: eventForm.venueId || '',
        guests: parseInt(eventForm.guests) || 0,
        stage: 'enquiry',
        notes: eventForm.notes || `Enquiry received via email on ${new Date(selectedEmail.date).toLocaleDateString('en-IE')}`,
        clientPhone: '',
        clientEmail: selectedEmail.from,
        startTime: '',
        endTime: '',
        setup: 'banquet',
        fbItems: [],
        roomHire: 0,
        packageTotal: 0,
        consumption: 0,
        billingType: 'group',
        depositPaid: false,
        contractSigned: false,
        contractDate: '',
        dietaryRequirements: '',
        setupRequirements: '',
        menuNotes: '',
      });
      eventId = newEvent.id;
      eventName = eventForm.eventName;
    }

    // Store the inbound email record linked to the event
    addSentEmail({
      id: `email_inbound_${Date.now()}`,
      eventId,
      eventName,
      templateId: null,
      templateName: 'Inbound Enquiry',
      to: 'events@errigalhotel.com',
      from: selectedEmail.from,
      subject: selectedEmail.subject,
      body: selectedEmail.body,
      sentAt: selectedEmail.date || new Date().toISOString(),
      status: 'received',
      source: 'inbound',
      auto: false,
      clientName: selectedEmail.fromName || selectedEmail.from,
      bodyPreview: (selectedEmail.body || '').substring(0, 120),
      direction: 'inbound',
    });

    // Mark as processed
    setProcessedIds((prev) => [...prev, selectedEmail.id]);
    setInboxEmails((prev) => prev.filter((em) => em.id !== selectedEmail.id));

    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setSelectedEmail(null);
      setStep(1);
      onClose();
    }, 1500);
  };

  if (!isOpen) return null;

  const formatTimeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-50">
      <div
        className="bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden"
        style={{ width: 720, maxHeight: '90vh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            <Inbox size={20} className="text-blue-600" />
            <h2 className="text-lg font-bold text-gray-900">Inbound Enquiries</h2>
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
              gmailStatus.connected ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
            }`}>
              {gmailStatus.label}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition"
          >
            <X size={20} />
          </button>
        </div>

        {/* Intake address info */}
        <div className="px-6 py-3 bg-blue-50 border-b border-blue-100 flex items-center gap-2">
          <Mail size={14} className="text-blue-600" />
          <span className="text-xs text-blue-800">
            Forward enquiry emails to <strong>events@errigalhotel.com</strong> to have them appear here
          </span>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {submitted ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                <Check size={28} className="text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-1">Enquiry Created</h3>
              <p className="text-sm text-gray-500">Email logged and event added to pipeline at Enquiry stage</p>
            </div>
          ) : step === 1 ? (
            /* Step 1: Inbox list */
            <>
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-500">
                  {inboxEmails.length > 0
                    ? `${inboxEmails.length} new enquiry email${inboxEmails.length !== 1 ? 's' : ''} waiting`
                    : 'No new enquiry emails found'}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowManualForm(!showManualForm)}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition"
                  >
                    <Plus size={14} />
                    Add Email Manually
                  </button>
                  <button
                    onClick={handleRefresh}
                    disabled={loading}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
                  >
                    <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                    {loading ? 'Checking...' : 'Refresh'}
                  </button>
                </div>
              </div>

              {/* Manual email form */}
              {showManualForm && (
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 mb-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">From Name</label>
                      <input
                        name="fromName"
                        value={manualEmailForm.fromName}
                        onChange={handleManualEmailFormChange}
                        placeholder="e.g., John Smith"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">From Email *</label>
                      <input
                        name="fromAddress"
                        type="email"
                        value={manualEmailForm.fromAddress}
                        onChange={handleManualEmailFormChange}
                        placeholder="e.g., john@example.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">Subject *</label>
                    <input
                      name="subject"
                      value={manualEmailForm.subject}
                      onChange={handleManualEmailFormChange}
                      placeholder="e.g., Wedding Enquiry - Summer 2027"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">Body</label>
                    <textarea
                      name="body"
                      value={manualEmailForm.body}
                      onChange={handleManualEmailFormChange}
                      placeholder="Email message..."
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">Date</label>
                    <input
                      name="date"
                      type="date"
                      value={manualEmailForm.date}
                      onChange={handleManualEmailFormChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-2">
                    <button
                      onClick={handleAddManualEmail}
                      disabled={addingEmail}
                      className="flex-1 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition disabled:opacity-50"
                    >
                      {addingEmail ? 'Adding...' : 'Save Email'}
                    </button>
                    <button
                      onClick={() => setShowManualForm(false)}
                      className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {loading && inboxEmails.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <RefreshCw size={32} className="text-gray-300 animate-spin mb-4" />
                  <p className="text-sm text-gray-400">Checking inbox...</p>
                </div>
              ) : inboxEmails.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Inbox size={40} className="text-gray-300 mb-4" />
                  <p className="text-gray-500 font-medium">Inbox is clear</p>
                  <p className="text-sm text-gray-400 mt-1">Forward an enquiry email to see it here</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {inboxEmails.map((email) => (
                    <button
                      key={email.id}
                      onClick={() => handleSelectEmail(email)}
                      className="w-full text-left p-4 rounded-lg border border-gray-200 hover:border-emerald-300 hover:bg-emerald-50 transition group"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            {!email.isRead && (
                              <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0"></span>
                            )}
                            <p className="font-semibold text-gray-900 truncate">
                              {email.fromName || email.from}
                            </p>
                          </div>
                          <p className="text-sm font-medium text-gray-700 mt-0.5 truncate">{email.subject}</p>
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">{email.snippet || (email.body || '').substring(0, 100)}</p>
                        </div>
                        <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                          <button
                            onClick={(e) => handleDeleteEmail(email, e)}
                            className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition"
                            title="Delete email"
                          >
                            <Trash2 size={16} />
                          </button>
                          <div className="flex flex-col items-end">
                            <span className="text-xs text-gray-400">{formatTimeAgo(email.date)}</span>
                            <ArrowRight size={16} className="text-gray-300 group-hover:text-emerald-500 mt-2 transition" />
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            /* Step 2: Create event from selected email */
            <>
              {/* Email preview */}
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 mb-5">
                <div className="flex items-center gap-2 mb-2">
                  <Mail size={16} className="text-blue-600" />
                  <span className="font-semibold text-gray-900 text-sm">{selectedEmail?.fromName || selectedEmail?.from}</span>
                  <span className="text-xs text-gray-400">&lt;{selectedEmail?.from}&gt;</span>
                </div>
                <p className="text-sm font-medium text-gray-800">{selectedEmail?.subject}</p>
                <p className="text-xs text-gray-500 mt-2 whitespace-pre-line line-clamp-4">{selectedEmail?.body}</p>
              </div>

              {/* Create new or link */}
              <div className="flex gap-3 mb-5">
                <button
                  onClick={() => setEventForm((p) => ({ ...p, linkToExisting: false }))}
                  className={`flex-1 px-4 py-3 rounded-lg border-2 text-sm font-medium transition text-center ${
                    !eventForm.linkToExisting
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                >
                  <UserPlus size={18} className="mx-auto mb-1" />
                  New Enquiry
                </button>
                <button
                  onClick={() => setEventForm((p) => ({ ...p, linkToExisting: true }))}
                  className={`flex-1 px-4 py-3 rounded-lg border-2 text-sm font-medium transition text-center ${
                    eventForm.linkToExisting
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                >
                  <ArrowRight size={18} className="mx-auto mb-1" />
                  Link to Existing
                </button>
              </div>

              {eventForm.linkToExisting ? (
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Select Event</label>
                  <select
                    name="existingEventId"
                    value={eventForm.existingEventId}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Choose an event...</option>
                    {events
                      .sort((a, b) => new Date(b.date) - new Date(a.date))
                      .map((evt) => (
                        <option key={evt.id} value={evt.id}>
                          {evt.name} — {evt.client} ({new Date(evt.date).toLocaleDateString('en-IE')})
                        </option>
                      ))}
                  </select>
                  <p className="text-xs text-gray-400 mt-2">The email will be stored in this event's email history.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Event Name *</label>
                      <input
                        name="eventName"
                        value={eventForm.eventName}
                        onChange={handleFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Event Type</label>
                      <select
                        name="eventType"
                        value={eventForm.eventType}
                        onChange={handleFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      >
                        <option value="private_party">Private Party</option>
                        <option value="corporate">Corporate</option>
                        <option value="wedding">Wedding</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Event Date</label>
                      <input
                        name="eventDate"
                        type="date"
                        value={eventForm.eventDate}
                        onChange={handleFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Venue</label>
                      <select
                        name="venueId"
                        value={eventForm.venueId}
                        onChange={handleFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      >
                        <option value="">Select...</option>
                        {venues.map((v) => (
                          <option key={v.id} value={v.id}>{v.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Guests</label>
                      <input
                        name="guests"
                        type="number"
                        value={eventForm.guests}
                        onChange={handleFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Notes</label>
                    <textarea
                      name="notes"
                      value={eventForm.notes}
                      onChange={handleFormChange}
                      rows={2}
                      placeholder="Any additional context..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!submitted && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="text-xs text-gray-500">
              {step === 1 ? (
                !gmailStatus.connected ? (
                  <span className="flex items-center gap-1">
                    <AlertCircle size={12} />
                    Demo mode: showing sample enquiries
                  </span>
                ) : 'Showing emails forwarded to your intake address'
              ) : 'Review and create the pipeline entry'}
            </div>
            <div className="flex items-center gap-3">
              {step === 2 && (
                <button
                  onClick={() => { setStep(1); setSelectedEmail(null); }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                >
                  Back
                </button>
              )}
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              {step === 2 && (
                <button
                  onClick={handleSubmit}
                  disabled={eventForm.linkToExisting ? !eventForm.existingEventId : !eventForm.eventName}
                  className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#1A5C3A' }}
                >
                  <Check size={16} />
                  {eventForm.linkToExisting ? 'Link to Event' : 'Create Enquiry'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InboundEmailModal;
